import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { Eye, QrCode, TrendingUp, Calendar, BarChart3, ArrowRight } from 'lucide-react'

export default function Analytics() {
  const { currentUser } = useAuth()
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [weeklyData, setWeeklyData] = useState([])

  useEffect(() => {
    async function fetchAnalytics() {
      if (!currentUser) return

      try {
        const analyticsDoc = await getDoc(doc(db, 'analytics', currentUser.uid))
        if (analyticsDoc.exists()) {
          const data = analyticsDoc.data()
          setAnalytics(data)
          
          // Generate Weekly Data (Last 7 Days)
          const days = []
          const today = new Date()
          for (let i = 6; i >= 0; i--) {
            const d = new Date(today)
            d.setDate(today.getDate() - i)
            const dateKey = `daily_${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}_${String(d.getDate()).padStart(2, '0')}`
            days.push({
              day: d.toLocaleDateString('en-US', { weekday: 'short' }),
              visits: data[dateKey] || 0
            })
          }
          setWeeklyData(days)
        }
      } catch (error) {
        console.error('Error fetching analytics:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [currentUser])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '50vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  // Calculate stats
  const totalVisits = analytics?.visits || 0
  const qrScans = analytics?.qrScans || 0
  const thisWeekVisits = weeklyData.reduce((acc, curr) => acc + curr.visits, 0)
  
  // Calculate max visits for chart scaling
  const maxVisits = Math.max(...weeklyData.map(d => d.visits), 1) // default to 1 to avoid divide by zero

  // Sources Data
  const sources = analytics?.sources || {}
  const totalSources = (sources.direct || 0) + (sources.qr || 0) + (sources.social || 0) || 1
  const directPercent = Math.round(((sources.direct || 0) / totalSources) * 100)
  const qrPercent = Math.round(((sources.qr || 0) / totalSources) * 100)
  const socialPercent = Math.round(((sources.social || 0) / totalSources) * 100)

  const stats = [
    { 
      icon: <Eye size={22} />, 
      label: 'Total Views', 
      value: totalVisits,
      change: 'Lifetime',
      positive: true 
    },
    { 
      icon: <QrCode size={22} />, 
      label: 'QR Scans', 
      value: qrScans,
      change: 'Lifetime',
      positive: true 
    },
    { 
      icon: <Calendar size={22} />, 
      label: 'This Week', 
      value: thisWeekVisits,
      change: 'Last 7 Days',
      positive: true 
    }
  ]

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
        <p className="page-subtitle">Track your vCard performance</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon">{stat.icon}</div>
              {stat.change && (
                <span className={`stat-change neutral`}>
                  {stat.change}
                </span>
              )}
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3 className="dashboard-card-title">Weekly Visits</h3>
          <div className="chart-container">
            {weeklyData.some(d => d.visits > 0) ? (
              <div className="bar-chart">
                {weeklyData.map((day, index) => (
                  <div key={index} className="bar-item">
                    <div 
                      className="bar" 
                      style={{ height: `${(day.visits / maxVisits) * 100}%` }}
                    >
                      <span className="bar-value">{day.visits > 0 ? day.visits : ''}</span>
                    </div>
                    <span className="bar-label">{day.day}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-chart">
                <BarChart3 size={32} className="text-gray-300 mb-2" />
                <p>No visits this week</p>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="dashboard-card-title">Traffic Sources</h3>
          <div className="traffic-sources">
            <div className="traffic-item">
              <div className="traffic-label">
                <span className="traffic-dot" style={{ background: 'var(--primary)' }}></span>
                Direct Link
              </div>
              <div className="traffic-bar">
                <div className="traffic-progress" style={{ width: `${directPercent}%`, background: 'var(--primary)' }}></div>
              </div>
              <span className="traffic-value">{directPercent}%</span>
            </div>
            <div className="traffic-item">
              <div className="traffic-label">
                <span className="traffic-dot" style={{ background: 'var(--success)' }}></span>
                QR Code
              </div>
              <div className="traffic-bar">
                <div className="traffic-progress" style={{ width: `${qrPercent}%`, background: 'var(--success)' }}></div>
              </div>
              <span className="traffic-value">{qrPercent}%</span>
            </div>
            <div className="traffic-item">
              <div className="traffic-label">
                <span className="traffic-dot" style={{ background: 'var(--warning)' }}></span>
                Social Media
              </div>
              <div className="traffic-bar">
                <div className="traffic-progress" style={{ width: `${socialPercent}%`, background: 'var(--warning)' }}></div>
              </div>
              <span className="traffic-value">{socialPercent}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="dashboard-card" style={{ marginTop: 'var(--space-lg)' }}>
        <h3 className="dashboard-card-title">Recent Activity</h3>
        {analytics?.lastActivityType ? (
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon">
                <Eye size={18} />
              </div>
              <div className="activity-content">
                <p>{analytics.lastActivityType}</p>
                <span>Last seen: {analytics.lastActivityTime?.toDate?.().toLocaleString() || new Date(analytics.lastActivityTime).toLocaleString()}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state" style={{ padding: 'var(--space-xl) 0' }}>
            <BarChart3 size={40} style={{ color: 'var(--gray-400)', marginBottom: 'var(--space-md)' }} />
            <p style={{ color: 'var(--text-muted)' }}>No recent activity recorded.</p>
          </div>
        )}
      </div>

      <style>{`
        .chart-container {
          padding: var(--space-md) 0;
          min-height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .empty-chart {
          display: flex;
          flex-direction: column;
          align-items: center;
          color: var(--text-muted);
          font-size: 0.9rem;
        }
        
        .bar-chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          height: 180px;
          width: 100%;
          gap: var(--space-md);
        }
        
        .bar-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          justify-content: flex-end;
        }
        
        .bar {
          width: 100%;
          max-width: 30px;
          background: linear-gradient(180deg, var(--primary), var(--primary-light));
          border-radius: var(--radius-sm) var(--radius-sm) 0 0;
          position: relative;
          min-height: 4px; /* Ensure tiny bars visible */
          transition: height 0.5s ease;
        }
        
        .bar-value {
          position: absolute;
          top: -20px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-dark);
        }
        
        .bar-label {
          margin-top: 8px;
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .stat-change.neutral {
          background: var(--gray-100);
          color: var(--text-muted);
        }
        
        .traffic-sources {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          padding: var(--space-md) 0;
        }
        
        .traffic-item {
          display: flex;
          align-items: center;
          gap: var(--space-md);
        }
        
        .traffic-label {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          width: 110px;
          font-size: var(--font-size-sm);
        }
        
        .traffic-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        
        .traffic-bar {
          flex: 1;
          height: 6px;
          background: var(--gray-100);
          border-radius: var(--radius-full);
          overflow: hidden;
        }
        
        .traffic-progress {
          height: 100%;
          border-radius: var(--radius-full);
          transition: width 0.5s ease;
        }
        
        .traffic-value {
          width: 35px;
          text-align: right;
          font-size: var(--font-size-sm);
          font-weight: 500;
        }
        
        .activity-list {
          display: flex;
          flex-direction: column;
        }
        
        .activity-item {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-md) 0;
          border-bottom: 1px solid var(--gray-100);
        }
        
        .activity-item:last-child {
          border-bottom: none;
        }
        
        .activity-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gray-100);
          color: var(--primary);
          border-radius: 50%;
        }
        
        .activity-content p {
          font-size: var(--font-size-sm);
          margin-bottom: 2px;
          font-weight: 500;
        }
        
        .activity-content span {
          font-size: var(--font-size-xs);
          color: var(--text-muted);
        }
      `}</style>
    </div>
  )
}
